import React, { useState, useEffect } from 'react';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import { ChevronDown, MapPin, Menu, Star, TrendingUp, Bell, Award, Users, Truck, Clock, Phone, Mail, Globe, Check } from 'lucide-react';

const FoodTruckLanding = () => {
  const [language, setLanguage] = useState('ar'); // Default to Arabic
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const controls = useAnimation();

  // RTL/LTR direction based on language
  useEffect(() => {
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  // Translations object
  const translations = {
    ar: {
      // Navigation
      nav: {
        home: 'الرئيسية',
        features: 'المميزات',
        plans: 'الخطط',
        contact: 'تواصل معنا'
      },
      // Hero section
      hero: {
        title: 'اكتشف أفضل عربات الطعام',
        subtitle: 'في المملكة العربية السعودية',
        description: 'تطبيقك المفضل للعثور على ألذ الوجبات المتنقلة بالقرب منك',
        download: 'حمل التطبيق',
        joinAsOwner: 'انضم كمالك',
        contact: 'تواصل معنا'
      },
      // Features section
      features: {
        title: 'مميزات التطبيق',
        forCustomers: 'للعملاء',
        forOwners: 'لمالكي العربات',
        customerFeatures: [
          { icon: MapPin, title: 'تتبع مباشر', desc: 'اعثر على عربات الطعام القريبة منك' },
          { icon: Menu, title: 'قوائم متنوعة', desc: 'استعرض القوائم والأسعار' },
          { icon: Star, title: 'تقييمات موثوقة', desc: 'اقرأ تقييمات العملاء الآخرين' },
          { icon: Clock, title: 'طلب مسبق', desc: 'اطلب وجبتك قبل الوصول' }
        ],
        ownerFeatures: [
          { icon: TrendingUp, title: 'تحليلات مفصلة', desc: 'تابع أداء عربتك' },
          { icon: Bell, title: 'إشعارات فورية', desc: 'استقبل الطلبات مباشرة' },
          { icon: Award, title: 'ترويج مميز', desc: 'اظهر في المقدمة' },
          { icon: Users, title: 'إدارة العملاء', desc: 'تواصل مع عملائك' }
        ]
      },
      // Plans section
      plans: {
        title: 'خطط الاشتراك لمالكي عربات الطعام',
        currency: 'ريال',
        perMonth: 'شهريًا',
        subscribe: 'اشترك الآن',
        plans: [
          {
            name: 'مجاني',
            price: 'مجاني',
            features: [
              'موقع مباشر على الخريطة',
              'عرض القائمة الأساسية',
              'دعم فني أساسي',
              'إحصائيات محدودة'
            ]
          },
          {
            name: 'برو',
            price: '٤٩',
            popular: true,
            features: [
              'جميع مميزات الخطة المجانية',
              'عروض ترويجية',
              'تحليلات متقدمة',
              'إشعارات فورية للعملاء',
              'دعم فني متقدم'
            ]
          },
          {
            name: 'بريميوم',
            price: '٩٩',
            features: [
              'جميع مميزات خطة برو',
              'ظهور مميز في النتائج',
              'عروض ترويجية غير محدودة',
              'دعم فني على مدار الساعة',
              'تقارير مخصصة'
            ]
          }
        ]
      },
      // Footer
      footer: {
        about: 'عن التطبيق',
        contact: 'تواصل معنا',
        followUs: 'تابعنا',
        rights: 'جميع الحقوق محفوظة HolaCloud© 2025'
      }
    },
    en: {
      // Navigation
      nav: {
        home: 'Home',
        features: 'Features',
        plans: 'Plans',
        contact: 'Contact'
      },
      // Hero section
      hero: {
        title: 'Discover the Best Food Trucks',
        subtitle: 'in Saudi Arabia',
        description: 'Your favorite app to find the most delicious mobile meals near you',
        download: 'Download App',
        joinAsOwner: 'Join as Owner',
        contact: 'Contact Us'
      },
      // Features section
      features: {
        title: 'App Features',
        forCustomers: 'For Customers',
        forOwners: 'For Truck Owners',
        customerFeatures: [
          { icon: MapPin, title: 'Live Tracking', desc: 'Find food trucks near you' },
          { icon: Menu, title: 'Diverse Menus', desc: 'Browse menus and prices' },
          { icon: Star, title: 'Trusted Reviews', desc: 'Read other customers reviews' },
          { icon: Clock, title: 'Pre-order', desc: 'Order your meal before arrival' }
        ],
        ownerFeatures: [
          { icon: TrendingUp, title: 'Detailed Analytics', desc: 'Track your truck performance' },
          { icon: Bell, title: 'Instant Notifications', desc: 'Receive orders directly' },
          { icon: Award, title: 'Featured Promotion', desc: 'Appear at the top' },
          { icon: Users, title: 'Customer Management', desc: 'Connect with your customers' }
        ]
      },
      // Plans section
      plans: {
        title: 'Subscription Plans for Food Truck Owners',
        currency: 'SAR',
        perMonth: '/month',
        subscribe: 'Subscribe Now',
        plans: [
          {
            name: 'Free',
            price: '0',
            features: [
              'Live location on map',
              'Basic menu display',
              'Basic support',
              'Limited statistics'
            ]
          },
          {
            name: 'Pro',
            price: '49',
            popular: true,
            features: [
              'All Free features',
              'Promotional offers',
              'Advanced analytics',
              'Push notifications',
              'Priority support'
            ]
          },
          {
            name: 'Premium',
            price: '99',
            features: [
              'All Pro features',
              'Featured placement',
              'Unlimited promotions',
              '24/7 priority support',
              'Custom reports'
            ]
          }
        ]
      },
      // Footer
      footer: {
        about: 'About App',
        contact: 'Contact Us',
        followUs: 'Follow Us',
        rights: 'All rights reserved © 2025'
      }
    }
  };

  const t = translations[language];

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const scaleIn = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: { scale: 1, opacity: 1 }
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Navigation Bar */}
      <nav className="fixed top-0 w-full bg-white/90 backdrop-blur-md shadow-sm z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <motion.div 
              initial={{ opacity: 0, x: language === 'ar' ? 20 : -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center space-x-2"
            >
              <Truck className="w-8 h-8 text-orange-500" />
              <span className="text-xl font-bold text-gray-800">
                {language === 'ar' ? 'فود تراك' : 'Food Truck'}
              </span>
            </motion.div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {Object.entries(t.nav).map(([key, value]) => (
                <motion.a
                  key={key}
                  href={`#${key}`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="text-gray-700 hover:text-orange-500 transition-colors"
                >
                  {value}
                </motion.a>
              ))}
              
              {/* Language Toggle */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setLanguage(language === 'ar' ? 'en' : 'ar')}
                className="flex items-center space-x-2 px-3 py-1 rounded-full bg-orange-100 text-orange-600 hover:bg-orange-200 transition-colors"
              >
                <Globe className="w-4 h-4" />
                <span className="text-sm font-medium">
                  {language === 'ar' ? 'EN' : 'ع'}
                </span>
              </motion.button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2"
            >
              <Menu className="w-6 h-6 text-gray-700" />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="md:hidden bg-white border-t"
            >
              <div className="container mx-auto px-4 py-4 space-y-4">
                {Object.entries(t.nav).map(([key, value]) => (
                  <a
                    key={key}
                    href={`#${key}`}
                    className="block text-gray-700 hover:text-orange-500 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {value}
                  </a>
                ))}
                <button
                  onClick={() => {
                    setLanguage(language === 'ar' ? 'en' : 'ar');
                    setIsMenuOpen(false);
                  }}
                  className="flex items-center space-x-2 px-3 py-1 rounded-full bg-orange-100 text-orange-600"
                >
                  <Globe className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    {language === 'ar' ? 'English' : 'عربي'}
                  </span>
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Hero Section */}
      <section id="home" className="pt-16 min-h-screen flex items-center relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 -z-10">
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 180, 360],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear"
            }}
            className="absolute top-10 right-10 w-96 h-96 bg-orange-100 rounded-full blur-3xl opacity-30"
          />
          <motion.div
            animate={{
              scale: [1.2, 1, 1.2],
              rotate: [360, 180, 0],
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: "linear"
            }}
            className="absolute bottom-10 left-10 w-96 h-96 bg-orange-200 rounded-full blur-3xl opacity-20"
          />
        </div>

        <div className="container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            {/* Hero Title */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-5xl md:text-7xl font-bold text-gray-800 mb-4"
            >
              {t.hero.title}
              <motion.span
                initial={{ opacity: 0, x: language === 'ar' ? -20 : 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="block text-orange-500 mt-2"
              >
                {t.hero.subtitle}
              </motion.span>
            </motion.h1>

            {/* Hero Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="text-xl text-gray-600 mb-10"
            >
              {t.hero.description}
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <motion.button
                variants={fadeInUp}
                whileHover={{ scale: 1.05, backgroundColor: '#ea580c' }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-orange-500 text-white rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
              >
                {t.hero.download}
                <ChevronDown className="w-5 h-5" />
              </motion.button>

              <motion.button
                variants={fadeInUp}
                whileHover={{ scale: 1.05, backgroundColor: '#f3f4f6' }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-white text-orange-500 border-2 border-orange-500 rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
              >
                {t.hero.joinAsOwner}
              </motion.button>

              <motion.button
                variants={fadeInUp}
                whileHover={{ scale: 1.05, backgroundColor: '#1f2937' }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-gray-800 text-white rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
              >
                {t.hero.contact}
              </motion.button>
            </motion.div>
          </div>
        </div>

        {/* Animated Scroll Indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
        >
          <ChevronDown className="w-8 h-8 text-orange-500" />
        </motion.div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          {/* Section Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              {t.features.title}
            </h2>
          </motion.div>

          {/* Customer Features */}
          <div className="mb-16">
            <motion.h3
              initial={{ opacity: 0, x: language === 'ar' ? 20 : -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-2xl font-semibold text-gray-700 mb-8 text-center"
            >
              {t.features.forCustomers}
            </motion.h3>
            
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              {t.features.customerFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  variants={scaleIn}
                  whileHover={{ scale: 1.05, translateY: -5 }}
                  className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <motion.div
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.5 }}
                    className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4"
                  >
                    <feature.icon className="w-8 h-8 text-orange-500" />
                  </motion.div>
                  <h4 className="text-xl font-semibold text-gray-800 mb-2">
                    {feature.title}
                  </h4>
                  <p className="text-gray-600">
                    {feature.desc}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Owner Features */}
          <div>
            <motion.h3
              initial={{ opacity: 0, x: language === 'ar' ? 20 : -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-2xl font-semibold text-gray-700 mb-8 text-center"
            >
              {t.features.forOwners}
            </motion.h3>
            
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              {t.features.ownerFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  variants={scaleIn}
                  whileHover={{ scale: 1.05, translateY: -5 }}
                  className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <motion.div
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.5 }}
                    className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4"
                  >
                    <feature.icon className="w-8 h-8 text-orange-500" />
                  </motion.div>
                  <h4 className="text-xl font-semibold text-gray-800 mb-2">
                    {feature.title}
                  </h4>
                  <p className="text-gray-600">
                    {feature.desc}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Subscription Plans Section */}
      <section id="plans" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          {/* Section Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              {t.plans.title}
            </h2>
          </motion.div>

          {/* Plans Grid */}
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto"
          >
            {t.plans.plans.map((plan, index) => (
              <motion.div
                key={index}
                variants={{
                  hidden: { opacity: 0, y: 50 },
                  visible: { 
                    opacity: 1, 
                    y: 0,
                    transition: {
                      delay: index * 0.1,
                      duration: 0.5
                    }
                  }
                }}
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: "0 20px 40px rgba(251, 146, 60, 0.3)"
                }}
                className={`relative bg-white rounded-3xl shadow-xl p-8 transition-all duration-300 ${
                  plan.popular ? 'border-2 border-orange-500' : 'border border-gray-200'
                }`}
              >
                {/* Popular Badge */}
                {plan.popular && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-orange-500 text-white px-4 py-1 rounded-full text-sm font-semibold"
                  >
                    {language === 'ar' ? 'الأكثر شعبية' : 'Most Popular'}
                  </motion.div>
                )}

                {/* Plan Name */}
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  {plan.name}
                </h3>

                {/* Price */}
                <div className="mb-6">
                  <span className="text-4xl font-bold text-orange-500">
                    {plan.price}
                  </span>
                  <span className="text-gray-600 mr-1">
                    {plan.price !== (language === 'ar' ? 'مجاني' : '0') && t.plans.currency}
                  </span>
                  <span className="text-gray-600">
                    {plan.price !== (language === 'ar' ? 'مجاني' : '0') && t.plans.perMonth}
                  </span>
                </div>

                {/* Features List */}
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <motion.li
                      key={featureIndex}
                      initial={{ opacity: 0, x: language === 'ar' ? 20 : -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.1 * featureIndex }}
                      className="flex items-start"
                    >
                      <Check className="w-5 h-5 text-green-500 mt-0.5 ml-2 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </motion.li>
                  ))}
                </ul>

                {/* CTA Button */}
                <motion.button
                  whileHover={{ 
                    scale: 1.05,
                    backgroundColor: plan.popular ? '#ea580c' : '#fb923c'
                  }}
                  whileTap={{ scale: 0.95 }}
                  className={`w-full py-3 rounded-full font-semibold transition-all duration-300 ${
                    plan.popular 
                      ? 'bg-orange-500 text-white hover:bg-orange-600' 
                      : 'bg-orange-100 text-orange-600 hover:bg-orange-200'
                  }`}
                >
                  {t.plans.subscribe}
                </motion.button>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* About */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h4 className="text-xl font-semibold mb-4">{t.footer.about}</h4>
              <p className="text-gray-400">
                {language === 'ar' 
                  ? 'منصة رائدة لربط عشاق الطعام بأفضل عربات الطعام في المملكة'
                  : 'Leading platform connecting food lovers with the best food trucks in the Kingdom'
                }
              </p>
            </motion.div>

            {/* Contact */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <h4 className="text-xl font-semibold mb-4">{t.footer.contact}</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span className="text-gray-400">+966 50 540 7677</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span className="text-gray-400">mshalrajeh@gmail.com</span>
                </div>
              </div>
            </motion.div>

            {/* Social Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <h4 className="text-xl font-semibold mb-4">{t.footer.followUs}</h4>
              <div className="flex gap-4">
                {['twitter', 'instagram', 'facebook'].map((social) => (
                  <motion.a
                    key={social}
                    href="#"
                    whileHover={{ scale: 1.2, rotate: 5 }}
                    whileTap={{ scale: 0.9 }}
                    className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center hover:bg-orange-600 transition-colors"
                  >
                    <span className="sr-only">{social}</span>
                    {/* Icon placeholder */}
                    <div className="w-5 h-5 bg-white rounded-sm"></div>
                  </motion.a>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Bottom Bar */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="mt-8 pt-8 border-t border-gray-800 text-center text-gray-400"
          >
            <p>{t.footer.rights}</p>
          </motion.div>
        </div>
      </footer>
    </div>
  );
};

export default FoodTruckLanding;